import { useCallback, useMemo, useState } from 'react';
import { CloseButton, Flex, Grid, GridContent, ProgressBar, Spinner } from '@zillow/constellation';
import { Heading, Modal } from '@zillow/constellation-10';
import {
  ActionMetadataEntryPoint,
  type GetTourConfigAndAvailabilityWebApiResponseDto,
  type TourConfirmationType,
} from '@zg-rentals/ts-z-calendar-client';

import TourCalendarConfig from './components/TourCalendarConfig';
import TourCalendarTimeZone from './components/TourCalendarTimeZone';
import TourAvailabilityCalendarFooter from './components/TourAvailabilityCalendarFooter';
import TourCalendar from '../TourCalendar';
import { type TimeZoneIds, TOUR_CALENDAR_MODES, TourWizardSteps } from '../../constants/TourCalendar';
import { MOBILE_HANDOFF_TYPES, mobileHandoffHandler } from '../../utils/mobileAppCallbacks';
import useTourCalendarApi from '../../utils/tourCalendarHooks';
import type { ClickStreamMetadata } from '../../constants/TouringClickStreamEvents';
import {
  CALENDAR_CLICKSTREAM_ACTION,
  emitCalendarEventActionEvent,
  emitTourAvailabilityWizardImpresssionEvent,
  emitTourAvailabilityWizardTimeZoneNextClickEvent,
  emitTourTypeSelectionEvent,
  TourAvailabilityWizardImpresssionSourceMap,
} from '../../constants/TouringClickStreamEvents';

type Props = {
  onClose: () => void;
  listingAlias: string;
  modalHeaderText: string;
  isInMobileWebView?: boolean;
  modalHeaderLevel?: 1 | 2 | 3 | 4 | 5 | 6;
  titleTag?: keyof JSX.IntrinsicElements;
  tourTitleTag?: keyof JSX.IntrinsicElements;
  clickStreamMetadata?: ClickStreamMetadata;
  hideCloseButton?: boolean;
};

const TourAvailabilityWizard = ({
  onClose,
  listingAlias,
  modalHeaderText,
  isInMobileWebView = false,
  modalHeaderLevel,
  titleTag,
  tourTitleTag,
  clickStreamMetadata,
  hideCloseButton = false,
}: Props) => {
  const mode = TOUR_CALENDAR_MODES.TOUR_AVAILABILITY;
  const [wizardStep, setWizardStep] = useState<TourWizardSteps>(TourWizardSteps.CONFIG);
  const [wizardTourConfirmationType, setWizardTourConfirmationType] = useState<TourConfirmationType>();
  const [wizardTimeZone, setWizardTimeZone] = useState<TimeZoneIds>();
  const [hasEnabledTouring, setHasEnabledTouring] = useState(false);

  const onGetTourConfigAndAvailabilitySuccess = useCallback(
    (data: GetTourConfigAndAvailabilityWebApiResponseDto) => {
      const trackTourAvailabilityWizardImpression = (source: TourAvailabilityWizardImpresssionSourceMap) => {
        if (clickStreamMetadata) {
          emitTourAvailabilityWizardImpresssionEvent(clickStreamMetadata, source);
        }
      };

      if ('tourConfig' in data && data.tourConfig) {
        const confirmationType = data.tourConfig.tourSchedulingTypes.schedulingTypes[0].confirmationType;
        const zoneId = data.tourConfig.zoneId;

        if (confirmationType && zoneId) {
          setWizardStep?.(TourWizardSteps.CALENDAR);
          setHasEnabledTouring(true);
          trackTourAvailabilityWizardImpression(TourAvailabilityWizardImpresssionSourceMap.TOUR_EVENT);
          return;
        }

        if (confirmationType) {
          setWizardStep?.(TourWizardSteps.TIME_ZONE);
          trackTourAvailabilityWizardImpression(TourAvailabilityWizardImpresssionSourceMap.PICK_TOUR_TIME_NEXT);
          return;
        }

        trackTourAvailabilityWizardImpression(TourAvailabilityWizardImpresssionSourceMap.PICK_TOUR_TYPE_NEXT);
        return;
      }

      trackTourAvailabilityWizardImpression(TourAvailabilityWizardImpresssionSourceMap.PICK_TOUR_TYPE_NEXT);
    },
    [setWizardStep],
  );

  const {
    availabilityEvents,
    tourEvents,
    timeZone: timeZoneFromApi,
    tourConfirmationType: tourConfirmationTypeFromApi,
    addAvailabilityEvent,
    updateAvailabilityEvent,
    removeAvailabilityEvent,
    performTourAction,
    enableTouringForListing,
    isGetTourConfigAndAvailabilityLoading,
    isEnableTouringForListingLoading,
    calendarStartTime,
    calendarEndTime,
  } = useTourCalendarApi({
    listingAlias,
    mode,
    onGetTourConfigAndAvailabilitySuccess,
  });

  const renderHeader = () => {
    return (
      <Heading level={modalHeaderLevel ?? 5} data-testid="TourAvailabilityWizard-ModalHeader">
        {modalHeaderText}
      </Heading>
    );
  };

  const mobileWebViewProps = useMemo(
    () => (isInMobileWebView ? { closeButton: null, header: null } : {}),
    [isInMobileWebView],
  );

  const onTourTypeSelection = (tourConfirmationType: TourConfirmationType) => {
    setWizardTourConfirmationType(tourConfirmationType);
  };

  const onTimeZoneSelection = (timeZone: TimeZoneIds) => {
    setWizardTimeZone(timeZone);
  };

  const renderBody = () => {
    let component;
    let progressBarValue = 1;

    if (wizardStep === TourWizardSteps.CONFIG) {
      component = (
        <TourCalendarConfig
          onTourTypeSelection={onTourTypeSelection}
          tourConfirmationType={wizardTourConfirmationType}
          titleTag={titleTag}
          tourTitleTag={tourTitleTag}
        />
      );
    } else if (wizardStep === TourWizardSteps.TIME_ZONE) {
      component = (
        <TourCalendarTimeZone
          onTimeZoneSelection={onTimeZoneSelection}
          timeZone={wizardTimeZone}
          titleTag={titleTag}
          disableTimeZoneSelection={Boolean(timeZoneFromApi)}
        />
      );
      progressBarValue = 2;
    } else if (timeZoneFromApi && tourConfirmationTypeFromApi) {
      component = (
        <TourCalendar
          availabilityEvents={availabilityEvents}
          tourEvents={tourEvents}
          addAvailabilityEvent={addAvailabilityEvent}
          updateAvailabilityEvent={updateAvailabilityEvent}
          removeAvailabilityEvent={removeAvailabilityEvent}
          performTourAction={performTourAction}
          mode={mode}
          titleTag={titleTag}
          timeZone={timeZoneFromApi}
          clickStreamMetadata={clickStreamMetadata}
          isInMobileWebView={isInMobileWebView}
          listingAlias={listingAlias}
          calendarStartTime={calendarStartTime}
          calendarEndTime={calendarEndTime}
        />
      );
      progressBarValue = 3;
    }

    return (
      <Grid display="grid" gridTemplateColumns="repeat(24, 1fr)">
        {!hasEnabledTouring ? (
          <GridContent
            gridColumnStart={{ default: '2', md: '4' }}
            gridColumnEnd={{ default: 'span 22', md: 'span 18' }}
            marginTop={{ default: 'sm', md: 'xl' }}
            marginBottom={{ default: 'md', md: 'lg' }}
            padding={0}
          >
            <ProgressBar maxValue={3} value={progressBarValue} data-testid="TourAvailabilityWizard-ProgressBar" />
          </GridContent>
        ) : (
          <GridContent
            gridColumnStart="1"
            gridColumnEnd="24"
            marginTop={{ default: isInMobileWebView ? undefined : 'sm', md: 'xl' }}
            padding={0}
          />
        )}
        {component}
      </Grid>
    );
  };

  const onClickBack = () => {
    if (wizardStep === TourWizardSteps.TIME_ZONE) {
      setWizardStep(TourWizardSteps.CONFIG);
    } else if (wizardStep === TourWizardSteps.CALENDAR) {
      setWizardStep(TourWizardSteps.TIME_ZONE);
    }
  };

  const onClickNext = () => {
    if (wizardStep === TourWizardSteps.CONFIG) {
      if (clickStreamMetadata && wizardTourConfirmationType) {
        emitTourTypeSelectionEvent(clickStreamMetadata, wizardTourConfirmationType);
      }
      return setWizardStep(TourWizardSteps.TIME_ZONE);
    } else if (wizardStep === TourWizardSteps.TIME_ZONE && wizardTourConfirmationType && wizardTimeZone) {
      return enableTouringForListing(wizardTourConfirmationType, wizardTimeZone, ActionMetadataEntryPoint.WIZARD).then(
        () => {
          if (clickStreamMetadata) {
            emitTourAvailabilityWizardTimeZoneNextClickEvent(clickStreamMetadata);
          }
          return setWizardStep(TourWizardSteps.CALENDAR);
        },
      );
    } else if (wizardStep === TourWizardSteps.CALENDAR) {
      if (clickStreamMetadata) {
        try {
          emitCalendarEventActionEvent(clickStreamMetadata, CALENDAR_CLICKSTREAM_ACTION.ADD_TO_LISTING_CTA);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error tracking wizard calendar cta', error);
        }
      }

      if (isInMobileWebView) {
        return mobileHandoffHandler(MOBILE_HANDOFF_TYPES.TOUR_ADDED_SUCCEEDED);
      }

      return onClose();
    }
  };

  const isNextStepDisabled = () => {
    if (wizardStep === TourWizardSteps.CONFIG) {
      return !wizardTourConfirmationType;
    } else if (wizardStep === TourWizardSteps.TIME_ZONE) {
      return !isEnableTouringForListingLoading && (!wizardTourConfirmationType || !wizardTimeZone);
    }

    return false;
  };

  if (isGetTourConfigAndAvailabilityLoading) {
    return (
      <Flex display="flex" justifyContent="center" padding="md">
        <Spinner />
      </Flex>
    );
  }

  return (
    <Modal
      open={true}
      // onClose={!hideCloseButton ? onClose : () => {}}
      onOpenChange={(open) => open === false && onClose()}
      closeButton={hideCloseButton ? null : <CloseButton />}
      header={renderHeader()}
      body={renderBody()}
      footer={
        <TourAvailabilityCalendarFooter
          isNextDisabled={isNextStepDisabled()}
          onClickBack={onClickBack}
          onClickNext={onClickNext}
          step={wizardStep}
          hasEnabledTouring={hasEnabledTouring}
        />
      }
      size="fullScreen"
      {...mobileWebViewProps}
    />
  );
};

export default TourAvailabilityWizard;
